/**
 * Support view: staff impersonation without the customer's email.
 *
 * Staff open a customer's workspace from Lightbase in one of two modes:
 *  - `limited` (support view): no approval needed, and no Gmail/Outlook-derived content. The backend enforces it
 *    (RLS + a route blocklist answering 403 `{"detail": {"error": "support_view_email_hidden"}}`); the app only
 *    avoids asking, and shows one calm placeholder instead of errors and spinners.
 *  - `full`: only while the customer approved a support-access request (D-16, "see your email").
 *
 * The mode arrives with the `/impersonate` handoff (`access_mode` hash param) and is confirmed with
 * `GET /user/support-session`; a 403 `support_view_email_hidden` anywhere also switches the tab to `limited`.
 * `null` means no restriction applies (not impersonating, or a session the backend doesn't treat as support).
 *
 * Pure helpers plus one small reactive store, persisted in sessionStorage like the impersonation state so a
 * reload keeps the mode. No Supabase / HTTP imports: the HTTP client and realtime import this.
 */
import { computed, ref } from 'vue'
import { IMPERSONATION_KEY } from './impersonationHeaders'

export type SupportAccessMode = 'limited' | 'full'

export interface SupportViewState {
    mode: SupportAccessMode | null
    /** When full access ends (ISO), when known. */
    until: string | null
}

/** `GET /user/support-session` for the caller's current session. */
export interface SupportSessionInfo {
    active: boolean
    access_mode: SupportAccessMode | null
    until: string | null
}

export const SUPPORT_VIEW_EMAIL_HIDDEN = 'support_view_email_hidden'
export const SUPPORT_VIEW_EMAIL_HIDDEN_TITLE = 'Email is hidden in support view.'
export const SUPPORT_VIEW_EMAIL_HIDDEN_DETAIL = 'Ask the customer to allow email access to see it.'
export const SUPPORT_VIEW_EMAIL_HIDDEN_MESSAGE = `${SUPPORT_VIEW_EMAIL_HIDDEN_TITLE} ${SUPPORT_VIEW_EMAIL_HIDDEN_DETAIL}`

const STORAGE_KEY = 'lh_support_view'
/** How long after a 403 `support_view_email_hidden` error toasts stay quiet (the callers' generic failures). */
const QUIET_WINDOW_MS = 5000

export function parseAccessMode(raw: unknown): SupportAccessMode | null {
    return raw === 'limited' || raw === 'full' ? raw : null
}

/**
 * The mode a `/impersonate` handoff carries. A handoff without `access_mode` (an older Lightbase) is treated as
 * `limited` until the backend confirms; the onboarding sandbox (staff's own test account) starts unrestricted.
 */
export function handoffAccessMode(params: URLSearchParams): SupportAccessMode | null {
    const mode = parseAccessMode(params.get('access_mode'))
    if (mode) return mode
    return params.get('sandbox') === '1' ? null : 'limited'
}

/** `GET /user/support-session` body → info, or null when it isn't one (old backend, junk). */
export function parseSupportSession(data: unknown): SupportSessionInfo | null {
    if (!data || typeof data !== 'object') return null
    const d = data as Record<string, unknown>
    if (typeof d.active !== 'boolean') return null
    const until = [d.until, d.granted_until, d.access_until].find((v) => typeof v === 'string' && v) as string | undefined
    return { active: d.active, access_mode: parseAccessMode(d.access_mode), until: until ?? null }
}

/**
 * Combine what the tab believes (`current`, from the handoff or an earlier check) with the backend's answer.
 * The backend wins when it answered: an active support session is `limited` unless it says `full`; a session it
 * doesn't know as support (`active: false`) has no restriction. No answer keeps `current`.
 */
export function resolveSupportViewMode(
    current: SupportAccessMode | null,
    server: SupportSessionInfo | null,
): SupportAccessMode | null {
    if (!server) return current
    if (!server.active) return null
    return server.access_mode ?? 'limited'
}

/** True for the backend's 403 `{"detail": {"error": "support_view_email_hidden"}}` (also tolerates flatter shapes). */
export function isSupportViewEmailHiddenResponse(status: number | undefined, data: unknown): boolean {
    if (status !== 403 || !data || typeof data !== 'object') return false
    const d = data as { detail?: unknown; error?: unknown }
    const detail = d.detail as { error?: unknown } | string | undefined
    if (detail && typeof detail === 'object' && detail.error === SUPPORT_VIEW_EMAIL_HIDDEN) return true
    return detail === SUPPORT_VIEW_EMAIL_HIDDEN || d.error === SUPPORT_VIEW_EMAIL_HIDDEN
}

/**
 * True for anything a caller may catch that stands for that 403: an axios-style error or resolved response
 * (`{status, data}` / `{response: {status, data}}`), or an error carrying `code` (AgentApiError).
 */
export function isSupportViewEmailHiddenError(err: unknown): boolean {
    if (!err || typeof err !== 'object') return false
    const e = err as { status?: unknown; data?: unknown; code?: unknown; response?: { status?: unknown; data?: unknown } }
    if (e.code === SUPPORT_VIEW_EMAIL_HIDDEN) return true
    if (e.response && isSupportViewEmailHiddenResponse(Number(e.response.status), e.response.data)) return true
    return isSupportViewEmailHiddenResponse(Number(e.status), e.data)
}

function hasImpersonation(): { sandbox: boolean } | null {
    try {
        const raw = sessionStorage.getItem(IMPERSONATION_KEY)
        if (!raw) return null
        return { sandbox: Boolean((JSON.parse(raw) as { sandbox?: boolean })?.sandbox) }
    } catch {
        return null
    }
}

function readStored(): SupportViewState {
    const none: SupportViewState = { mode: null, until: null }
    if (typeof window === 'undefined') return none
    const imp = hasImpersonation()
    if (!imp) return none
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY)
        if (raw) {
            const s = JSON.parse(raw) as Partial<SupportViewState>
            return { mode: parseAccessMode(s.mode), until: typeof s.until === 'string' ? s.until : null }
        }
    } catch {
        /* fall through */
    }
    // An impersonation stored before this existed: hide email until the backend says otherwise.
    return { mode: imp.sandbox ? null : 'limited', until: null }
}

const state = ref<SupportViewState>(readStored())
let lastHiddenAt = 0

export const supportViewMode = computed(() => state.value.mode)
export const supportViewUntil = computed(() => state.value.until)
/** True while email content is hidden from this tab (support view). */
export const supportViewLimited = computed(() => state.value.mode === 'limited')

export function isSupportViewLimited(): boolean {
    return state.value.mode === 'limited'
}

export function setSupportViewState(next: SupportViewState): void {
    const value: SupportViewState = { mode: parseAccessMode(next.mode), until: next.until || null }
    if (value.mode === state.value.mode && value.until === state.value.until) return
    state.value = value
    try {
        if (hasImpersonation()) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value))
    } catch {
        /* private mode: memory only */
    }
}

export function clearSupportViewState(): void {
    state.value = { mode: null, until: null }
    lastHiddenAt = 0
    try {
        sessionStorage.removeItem(STORAGE_KEY)
    } catch {
        /* ignore */
    }
}

/** The backend refused email content (403 `support_view_email_hidden`): this tab is in support view. */
export function noteSupportViewEmailHidden(now = Date.now()): void {
    lastHiddenAt = now
    if (state.value.mode !== 'limited') setSupportViewState({ mode: 'limited', until: null })
}

/** Whether an error toast now is most likely the caller's generic reaction to that 403 (keep it quiet). */
export function supportViewQuietErrors(now = Date.now()): boolean {
    return lastHiddenAt > 0 && now - lastHiddenAt < QUIET_WINDOW_MS
}
